// Game constants and configurations
import { GameVariant, Suit, Rank } from '../types';
// Card game configurations for each variant
export const GAME_CONFIGS = {
    [GameVariant.KADI_NI_TIDI]: {
        name: 'Kadi ni Tidi',
        displayName: '3 of Spades',
        description: 'Traditional Gujarati card game with secret partnerships',
        maxPlayers: 10, // Updated from 4 to support 4-10 players
        minPlayers: 4,
        deckSize: 52,
        cardsPerPlayer: 13, // Base configuration, varies by player count
        bidRange: [125, 250],
        bidIncrement: 5,
        specialCards: {
            threeOfSpades: { suit: Suit.SPADES, rank: Rank.THREE, points: 30 },
        },
        pointSystem: {
            [Rank.ACE]: 10,
            [Rank.KING]: 10,
            [Rank.QUEEN]: 10,
            [Rank.JACK]: 10,
            [Rank.TEN]: 10,
            [Rank.FIVE]: 5,
            // All other cards: 0 points
        },
        features: ['secret_partnerships', 'bidding', 'trump_selection', 'trick_taking'],
        difficulty: 'hard',
        estimatedGameTime: 30, // minutes
        region: 'Gujarat, Western India',
        language: 'Gujarati',
    },
    [GameVariant.THREE_ZERO_FOUR]: {
        name: '304',
        displayName: 'Three-Nought-Four',
        description: 'Popular Indian card game with marriage bonuses',
        maxPlayers: 4,
        deckSize: 32,
        cardsPerPlayer: 8,
        bidRange: [150, 304],
        bidIncrement: 1,
        cardHierarchy: [Rank.JACK, Rank.NINE, Rank.ACE, Rank.TEN, Rank.KING, Rank.QUEEN, Rank.EIGHT, Rank.SEVEN],
        marriageBonuses: {
            trump: 40,
            nonTrump: 20,
        },
        features: ['partnerships', 'bidding', 'marriages', 'trick_taking'],
        difficulty: 'hard',
        estimatedGameTime: 25,
        region: 'Pan-India',
        language: 'Hindi',
    },
    [GameVariant.TWENTY_EIGHT]: {
        name: 'Twenty-Eight',
        displayName: 'Irupathiyettu',
        description: 'South Indian partnership card game',
        maxPlayers: 4,
        deckSize: 32,
        cardsPerPlayer: 8,
        bidRange: [14, 28],
        bidIncrement: 1,
        features: ['fixed_partnerships', 'bidding', 'trick_taking'],
        difficulty: 'medium',
        estimatedGameTime: 20,
        region: 'South India',
        language: 'Tamil',
    },
    [GameVariant.MENDIKOT]: {
        name: 'Mendikot',
        displayName: 'Capture the Tens',
        description: 'Card game focused on capturing Ten cards',
        maxPlayers: 4,
        deckSize: 52,
        cardsPerPlayer: 13,
        objective: 'capture_tens',
        winConditions: {
            mendikot: 'all_four_tens',
            alternative: 'seven_consecutive_tricks',
        },
        features: ['partnerships', 'tens_capture', 'dynamic_trump'],
        difficulty: 'medium',
        estimatedGameTime: 25,
        region: 'North India',
        language: 'Hindi',
    },
    [GameVariant.DEHLA_PAKAD]: {
        name: 'Dehla Pakad',
        displayName: 'Capture the Ten',
        description: 'Kots-based scoring system',
        maxPlayers: 4,
        deckSize: 52,
        cardsPerPlayer: 13,
        scoringSystem: 'kots',
        features: ['partnerships', 'tens_capture', 'kots_scoring'],
        difficulty: 'medium',
        estimatedGameTime: 20,
        region: 'Punjab',
        language: 'Punjabi',
    },
    [GameVariant.THREE_TWO_FIVE]: {
        name: '3-2-5',
        displayName: 'Three-Two-Five',
        description: 'Three-player quota-based game',
        maxPlayers: 3,
        deckSize: 30,
        cardsPerPlayer: 10,
        quotas: [3, 2, 5],
        features: ['individual_play', 'quotas', 'no_partnerships'],
        difficulty: 'easy',
        estimatedGameTime: 15,
        region: 'General',
        language: 'English',
    },
};
// Regional game categories
export const GAME_REGIONS = {
    GUJARATI: [GameVariant.KADI_NI_TIDI],
    NORTH_INDIAN: [GameVariant.THREE_ZERO_FOUR, GameVariant.MENDIKOT],
    SOUTH_INDIAN: [GameVariant.TWENTY_EIGHT],
    PUNJABI: [GameVariant.DEHLA_PAKAD],
    GENERAL: [GameVariant.THREE_TWO_FIVE],
};
// Difficulty levels
export const DIFFICULTY_LEVELS = {
    EASY: ['3-2-5'],
    MEDIUM: ['twenty-eight', 'mendikot', 'dehla-pakad'],
    HARD: ['kadi-ni-tidi', '304'],
};
// Point values for different card ranks (default system)
export const DEFAULT_CARD_POINTS = {
    [Rank.ACE]: 11,
    [Rank.TEN]: 10,
    [Rank.KING]: 4,
    [Rank.QUEEN]: 3,
    [Rank.JACK]: 2,
    [Rank.NINE]: 0,
    [Rank.EIGHT]: 0,
    [Rank.SEVEN]: 0,
    [Rank.SIX]: 0,
    [Rank.FIVE]: 0,
    [Rank.FOUR]: 0,
    [Rank.THREE]: 0,
    [Rank.TWO]: 0,
};
// Game phases duration (in seconds)
export const PHASE_TIMEOUTS = {
    BIDDING: 30,
    TRUMP_SELECTION: 15,
    PARTNER_CALLING: 20,
    CARD_PLAY: 20,
    BETWEEN_TRICKS: 5,
};
// Matchmaking settings
export const MATCHMAKING_CONFIG = {
    TIMEOUT: 60000, // 1 minute
    SKILL_RANGE: 100, // rating difference
    EXPAND_RANGE_INTERVAL: 10000, // 10 seconds
    MAX_SKILL_RANGE: 500,
};
// Tournament configurations
export const TOURNAMENT_CONFIGS = {
    MIN_PARTICIPANTS: {
        SINGLE_ELIMINATION: 4,
        DOUBLE_ELIMINATION: 4,
        ROUND_ROBIN: 3,
        SWISS: 6,
    },
    MAX_PARTICIPANTS: {
        SINGLE_ELIMINATION: 64,
        DOUBLE_ELIMINATION: 32,
        ROUND_ROBIN: 12,
        SWISS: 128,
    },
};
// Cultural and festival themes
export const CULTURAL_THEMES = {
    FESTIVALS: {
        DIWALI: {
            name: 'Diwali',
            colors: ['#FFD700', '#FF6B35', '#FF8C42'],
            period: 'October-November',
            games: [GameVariant.KADI_NI_TIDI, GameVariant.THREE_ZERO_FOUR],
        },
        HOLI: {
            name: 'Holi',
            colors: ['#FF69B4', '#00CED1', '#FFD700'],
            period: 'March',
            games: [GameVariant.TWENTY_EIGHT, GameVariant.MENDIKOT],
        },
        EID: {
            name: 'Eid',
            colors: ['#008000', '#FFD700', '#FFFFFF'],
            period: 'Variable',
            games: [GameVariant.DEHLA_PAKAD],
        },
    },
};
// Achievement categories and requirements
export const ACHIEVEMENT_CONFIGS = {
    BEGINNER: {
        FIRST_GAME: { games: 1, reward: 100 },
        FIRST_WIN: { wins: 1, reward: 200 },
        GAMES_PLAYED_10: { games: 10, reward: 500 },
    },
    INTERMEDIATE: {
        GAMES_PLAYED_50: { games: 50, reward: 1000 },
        GAMES_WON_25: { wins: 25, reward: 1500 },
        WIN_STREAK_5: { streak: 5, reward: 2000 },
    },
    ADVANCED: {
        GAMES_PLAYED_200: { games: 200, reward: 3000 },
        GAMES_WON_100: { wins: 100, reward: 4000 },
        WIN_STREAK_10: { streak: 10, reward: 5000 },
    },
    MASTER: {
        PERFECT_GAME: { special: 'perfect_bid', reward: 10000 },
        CULTURAL_MASTER: { cultural_games: 50, reward: 7500 },
        TOURNAMENT_WINNER: { tournaments: 1, reward: 15000 },
    },
};
// Error codes and messages
export const ERROR_CODES = {
    // Authentication errors
    INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    UNAUTHORIZED: 'UNAUTHORIZED',
    // Game errors
    GAME_NOT_FOUND: 'GAME_NOT_FOUND',
    ROOM_FULL: 'ROOM_FULL',
    INVALID_MOVE: 'INVALID_MOVE',
    NOT_YOUR_TURN: 'NOT_YOUR_TURN',
    GAME_ALREADY_STARTED: 'GAME_ALREADY_STARTED',
    // Validation errors
    INVALID_INPUT: 'INVALID_INPUT',
    MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',
    // Server errors
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
};
// WebSocket event types
export const WS_EVENTS = {
    // Connection events
    CONNECT: 'connect',
    DISCONNECT: 'disconnect',
    RECONNECT: 'reconnect',
    // Room events
    JOIN_ROOM: 'join_room',
    LEAVE_ROOM: 'leave_room',
    ROOM_UPDATE: 'room_update',
    // Game events
    GAME_START: 'game_start',
    GAME_UPDATE: 'game_update',
    GAME_END: 'game_end',
    PLAYER_ACTION: 'player_action',
    // Chat events
    CHAT_MESSAGE: 'chat_message',
    CHAT_HISTORY: 'chat_history',
    // Matchmaking events
    MATCH_FOUND: 'match_found',
    MATCH_CANCELLED: 'match_cancelled',
};
//# sourceMappingURL=games.js.map